/**
 * Controller for all ticketing interactions
 *
 * @module ticketing/controller
 */

import qs from 'qs';
import Store from 'store/store';
import * as actions from 'store/actions';
import {
	isDemoTicketing,
	gateways,
	gatewayConfigs,
	defaultGateway,
	seatStatus,
	mxErrors,
	getMxErrorKey,
	isMxErrorObject,
	powErrors,
	getPowErrorKey,
	isPowErrorObject,
	getFallbackUrl,
	getTicketsText,
	areSeatPositionsEqual,
	areSeatsEqual,
	isWheelchair,
	isCompanionSeat,
	isSeatForDisablePeople,
	getSeats,
	getPickedSeats,
	findWheelchairInGroup,
	getSeatLayoutBoundingBox,
	getIframeCommunicatorUrl,
	checkIframeCommunicatorUrl
} from 'store/ticketingPublic';
import { track } from 'utils/tracking';
import TimeTracker from 'utils/timeTracker';
import { getCopy } from 'utils/copy';
import { isObject, accessNested } from 'utils/utils';
import * as mxApi from 'services/mxApi';


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Constants

/** UI States */
export const NOT_ACTIVE      = 'NOT_ACTIVE';
export const SELECT_TICKETS  = 'SELECT_TICKETS';
export const SELECT_SEATS    = 'SELECT_SEATS';
export const REVIEW          = 'REVIEW';
export const CONTACT_DETAILS = 'CONTACT_DETAILS';
export const PAYMENT         = 'PAYMENT';
export const COMPLETE        = 'COMPLETE';
export const ERROR           = 'ERROR';

/** Order of UI States */
export const ticketingOrder = [NOT_ACTIVE, SELECT_TICKETS, SELECT_SEATS, REVIEW, CONTACT_DETAILS,
	PAYMENT, COMPLETE];

/** The number of decimals for the heatmap position vectors */
const HEATMAP_FLOAT_PRECISION = 7;

/** The number of decimals for the theater occupancy */
const OCCUPANCY_FLOAT_PRECISION = 4;

/** The default time allowed to book a ticket (seconds) */
const DEFAULT_BOOKING_TIMEOUT_S = 300;

/** Tracking of loading times */
const timeTracker = new TimeTracker('ticketing-');

const DWELL_TIME                     = 'dwell-time';
const LOADING_TIME_TICKET_TYPES      = 'loading-time-ticket-types';
const LOADING_TIME_CREATE_ORDER      = 'loading-time-create-order';
const LOADING_TIME_UPDATE_ORDER      = 'loading-time-update-order';
const LOADING_TIME_UPDATE_SEATS      = 'loading-time-update-seats';
const LOADING_TIME_FLUSH_LAYOUT      = 'loading-time-flush-layout';
const LOADING_TIME_PAYMENT_PRECHECK  = 'loading-time-payment-precheck';
const LOADING_TIME_START_TRANSACTION = 'loading-time-start-transaction';
const LOADING_TIME_PAYMENTS          = 'loading-time-payments';
const LOADING_TIME_COMPLETE_ORDER    = 'loading-time-complete-order';


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Event list

Store.on(actions.OPEN_TICKETING              , openTicketing);
Store.on(actions.REOPEN_TICKETING            , reopenTicketing);
Store.on(actions.GO_TICKETING_TYPES          , goToTicketingTypes);
Store.on(actions.UPDATE_TICKET_TYPE_QUANTITY , updateTicketTypeQuantity);
Store.on(actions.CREATE_OR_UPDATE_ORDER      , createOrUpdateOrder);
Store.on(actions.GO_TICKETING_SEATING        , goToTicketingSeating);
Store.on(actions.PICK_OR_UNPICK_SEATS        , pickOrUnpickSeats);
Store.on(actions.UPDATE_TICKETING_SEATS      , updateTicketingSeats);
Store.on(actions.FLUSH_SEAT_LAYOUT           , flushSeatLayout);
Store.on(actions.GO_TICKETING_REVIEW         , goToTicketingReview);
Store.on(actions.GO_TICKETING_CONTACT_DETAILS, goToTicketingContactDetails);
Store.on(actions.GO_TICKETING_PAYMENT        , goToTicketingPaymentPreCheck);
Store.on(actions.PAYMENT_MESSAGE_RECEIVED    , onPaymentMessageReceived);
Store.on(actions.SET_TICKETING_MESSAGE       , setTicketingMessage);
Store.on(actions.CLEAR_TICKETING_MESSAGE     , clearTicketingMessage);
Store.on(actions.GO_TICKETING_TIMEOUT        , goToTicketingTimeout);
Store.on(actions.CLOSE_TICKETING             , closeTicketing);
Store.on(actions.CLOSE_APP                   , closeApp);


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Payment gateways constants

// Authorize.net speicific messages while in payment
const AUTHNET_MSG_RESIZE_WINDOW        = 'resizeWindow';
const AUTHNET_MSG_CANCEL               = 'cancel';
const AUTHNET_MSG_TRANSACTION_RESPONSE = 'transactResponse';


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Ticketing sessions

/**
 * The current ticketing session identifier.
 * This identifier will ensure that any response we're receiving from the server is about the
 * current session and not a previously created session.<br>
 * <br>
 * For example, if you send a request to create an order and then open a new ticketing session
 * before you actually receive the response, you need to ignore the response when it arrives,
 * because the response is about the outdated session.
 */
let _sessionId = 1;

/**
 * Open a new ticketing session:
 * <ol>
 *   <li>create a new ticketing state object in `showtimes`</li>
 *   <li>display the "SELECT_TICKETS" pane</li>
 * </ol>
 *
 * @param {Object} data - data received by the "OPEN_TICKETING" event
 * @property {Theater} data.theater - the currently active theater
 * @property {Screening} data.screening - the currently active screening
 */
function openTicketing({ theater, screening }) {
	const ticketing = Store.get().showtimes.ticketing;

	// TODO: We MUST check if a ticketing session is already opened, then close it.
	//       event if everything has been built to avoid this situation!

	track({ a: 'ticketing-open' });
	timeTracker.start(DWELL_TIME);

	let seatingSupported = accessNested(screening, 'doc.isAllocatedSeating');
	if (typeof seatingSupported === 'undefined') {
		seatingSupported = !!accessNested(screening, 'doc.is_allocated_seating', true);
	}

	const { exhibitor } = theater;

	let paymentGateway = accessNested(exhibitor, 'doc.payment.gateway', defaultGateway);
	let loyaltyEnabled = accessNested(exhibitor, 'doc.ticketing.loyalty.enabled', false);
	let contactFields  = accessNested(exhibitor, 'doc.ticketing.contactFields', {});
	let termsUrl       = accessNested(exhibitor, 'doc.legals.termsUrl');
	let privacyUrl     = accessNested(exhibitor, 'doc.legals.privacyUrl');

	let termsOwner   = exhibitor.name;
	let privacyOwner = exhibitor.name;

	// Override exhibitor properties with theater's own properties
	const theaterPaymentGateway = accessNested(theater, 'masterDoc.payment.gateway');
	const theaterLoyaltyEnabled = accessNested(theater, 'masterDoc.ticketing.loyalty.enabled');
	const theaterContactFields  = accessNested(theater, 'masterDoc.ticketing.contactFields');
	const theaterTermsUrl       = accessNested(theater, 'masterDoc.legals.termsUrl');
	const theaterPrivacyUrl     = accessNested(theater, 'masterDoc.legals.privacyUrl');

	if (typeof theaterPaymentGateway !== 'undefined') paymentGateway = theaterPaymentGateway;
	if (typeof theaterLoyaltyEnabled !== 'undefined') loyaltyEnabled = !!theaterLoyaltyEnabled;
	if (typeof theaterContactFields  !== 'undefined') contactFields = theaterContactFields;

	if (typeof theaterTermsUrl !== 'undefined') {
		termsUrl   = theaterTermsUrl;
		termsOwner = theater.name;
	}
	if (typeof theaterPrivacyUrl !== 'undefined') {
		privacyUrl   = theaterPrivacyUrl;
		privacyOwner = theater.name;
	}

	if (!termsUrl) console.warn('There is no terms and conditions link setup for this exhibitor');
	if (!privacyUrl) console.warn('There is no privacy policy link setup for this exhibitor');

	// Override payment gateway and seating in demo mode
	if (isDemoTicketing()) {
		const params = qs.parse(window.location.search.substr(1));
		if (params.seating !== undefined) seatingSupported = params.seating !== 'false';
		if (params.gateway !== undefined) paymentGateway = params.gateway;
	}

	ticketing.set({
		sessionId:        _sessionId,
		theaterId:        theater.id,
		theaterMxId:      theater.providerTheaterId,
		exhibitorId:      exhibitor.id,
		screeningId:      screening.id,
		screeningMxId:    screening.provider_screening_id,
		// ^ NOTE: screening.id and screening.provider_screening_id used to be the same.
		//         For all MX related stuff, use the screeningMxId
		meta: {
			movieTitle:     accessNested(screening, 'movie.title'),
			movieFormat:    accessNested(screening, 'movie.format.label'),
			movieDate:      screening.nextDayDatetime || screening.datetime,
			theaterName:    theater.name,
			theaterAddress: accessNested(theater, 'location.fullAddress'),
			exhibitorName:  exhibitor.name,
			termsUrl:       termsUrl,
			termsOwner:     termsOwner,
			privacyUrl:     privacyUrl,
			privacyOwner:   privacyOwner
		},
		state:            SELECT_TICKETS,
		seatingSupported: seatingSupported,
		paymentGateway:   paymentGateway,
		loyaltyEnabled:   loyaltyEnabled,
		contactFields:    contactFields,
		startDate:        Date.now(),
		savedTickets:     null,
		fallbackUrl:      getFallbackUrl(theater, screening),
		isDemo:           isDemoTicketing()
	});

	Store.emit(actions.GO_TICKETING_TYPES);
}

/**
 * Reopen a previously opened ticketing session
 * The session will only be reopened if the session was opened before (= has all the necessary
 * infos available).
 *
 * This function will redirect the user to the ticket booking or the seating section based on the
 * previous state.
 */
function reopenTicketing() {
	let ticketing = Store.get().showtimes.ticketing;

	// If session is up to date, no need to reopen
	if (ticketing.sessionId === _sessionId) return;

	// Check that basic info are there
	if (!ticketing.theaterId || !ticketing.exhibitorId) return;
	if (!ticketing.screeningMxId && !isDemoTicketing()) return;

	if (!Object.prototype.hasOwnProperty.call(ticketing, 'seatingSupported')) return;
	if (!Object.prototype.hasOwnProperty.call(ticketing, 'paymentGateway')) return;

	// Reopen accepted: update the sessionId
	ticketing = ticketing.set({
		sessionId: _sessionId,
		startDate: Date.now()
	});

	// Recover saved tickets
	if (Object.prototype.hasOwnProperty.call(ticketing, 'savedTickets') && isObject(ticketing.savedTickets)) {
		ticketing = ticketing.set('tickets', ticketing.savedTickets.toJS());
	}

	// Redirect the user:
	// if there are some selected seats, we can directly go to the seating pane.
	const hasAlreadyBookedTickets = Object.prototype.hasOwnProperty.call(ticketing, 'pickedSeats');
	Store.emit(hasAlreadyBookedTickets ? actions.GO_TICKETING_SEATING : actions.GO_TICKETING_TYPES);
}

/**
 * Clear the ticketing session:
 * (1) cancel the order, if any
 * (2) reset the ticketing state
 *
 * This function does not make any change to the UI, you need to redirect the user afterward.
 *
 * @param {boolean=} async - whether the cancel order request should be asynchronous or not
 * @return {boolean} true if an order was cancelled, false otherwise
 */
function clearTicketingSession(async = true) {
	let orderCancelled = false;

	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;

	const { exhibitorId, order } = ticketing;

	// User just left ticketing
	timeTracker.end(DWELL_TIME);

	// Make sure the loading is closed
	ticketing = ticketing.set({
		apiLoading:         false,
		apiMessage:         '',
		apiMessageCallback: undefined
	});

	// Cancel the order in case it was created but not complete
	const orderComplete = !!accessNested(ticketing, 'payment.bookingId');
	if (ticketing.order && !orderComplete) {
		mxApi.cancelOrder(exhibitorId, order.orderId, authToken, async);
		orderCancelled = true;

		// Save tickets for eventual future recovering
		if (Object.prototype.hasOwnProperty.call(ticketing, 'tickets') && isObject(ticketing.tickets)) {
			ticketing = ticketing.set('savedTickets', ticketing.tickets.toJS());
		}

		ticketing = ticketing.remove(['order', 'timeout', 'allowedTime', 'startDate']);
	}

	if (ticketing.tickets) ticketing = ticketing.remove('tickets');

	// Clear all pending loading times
	timeTracker.clearAll();

	_sessionId++;
	// ^ now this session is now outdated until we call openTicketing() OR reopenTicketing()

	return orderCancelled;
}

/**
 * Set the ticketing message to display to the user.
 *
 * @param {!Object} data - the event data
 * @property {!string} data.message - the message to show to the user
 * @property {Function=} data.callback - optional function to call when the message box is closed
 */
function setTicketingMessage({ message, callback }) {
	if (callback && typeof callback !== 'function') {
		callback = undefined;
	}
	Store.get().showtimes.ticketing.set({
		apiMessage: message,
		apiMessageCallback: callback
	});
}

/**
 * Hide the ticketing message.
 * This function will clear both the `apiMessage` and the `apiMessageCallback`.
 */
function clearTicketingMessage() {
	Store.get().showtimes.ticketing.set({
		apiMessage: '',
		apiMessageCallback: undefined
	});
}

/**
 * Close the ticketing pane.
 * This function clears the ticketing session and prepare the ticketing state for the next session.
 *
 * Note that, after calling this function, the ticketing state will still exist to keep the
 * previously ordered tickets (if any), so you don't have to re-enter your tickets again.
 */
function closeTicketing() {
	let ticketing = Store.get().showtimes.ticketing;
	const wasLoading = ticketing.apiLoading;
	const lastStep = ticketing.state;

	if (!lastStep || lastStep === NOT_ACTIVE) return;

	track({ a: 'ticketing-close' });

	if (wasLoading) {
		track({ a: 'ticketing-close-while-loading' });
	}

	clearTicketingSession();

	let showtimes = Store.get().showtimes;
	ticketing = showtimes.ticketing;

	const newTicketing = {
		sessionId: ticketing.sessionId,
		state:     NOT_ACTIVE,
		authToken: ticketing.authToken
	};

	// Add in the previously ordered tickets, if any
	if (ticketing.tickets) {
		newTicketing.tickets = ticketing.tickets;
	}

	if (lastStep === SELECT_SEATS && ticketing.seatingSupported && ticketing.seating) {
		// Here, the user might give up because there's not enough free seats
		// So let's track the theater's occupancy
		track({
			a: 'ticketing-close-occupancy',
			l: getTheaterOccupancy(ticketing.seating).toFixed(OCCUPANCY_FLOAT_PRECISION)
		});
	}

	showtimes = showtimes.remove('ticketing');
	showtimes.set('ticketing', newTicketing);
}

/**
 * Called when the page is closed.
 * This function cancel a previously created order synchronously to make sure the request is sent
 * before the page is closed.
 */
function closeApp() {
	const ticketing = accessNested(Store.get(), 'showtimes.ticketing');
	if (!ticketing) return;

	if (!ticketing.state || ticketing.state === NOT_ACTIVE) return;

	track({ a: 'ticketing-close' });
	track({ a: 'ticketing-close-app' });

	if (ticketing.order) {
		clearTicketingSession(false);
		// ^ synchronous, but intentional
	}
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Ticket types

/**
 * Loads ticket types for the screening stored in the ticketing state then
 * display the "SELECT_TICKETS" pane, with the tickets available for that screening.
 */
function goToTicketingTypes() {
	const store = Store.get();
	const { authToken } = store.user;
	const { ticketing } = store.showtimes;

	if (ticketing.ticketTypes) {
		// We already have ticket types, no need to ask for them again
		ticketing.set({ state: SELECT_TICKETS });
		return;
	}

	const { exhibitorId, screeningMxId } = ticketing;

	ticketing.set({
		state:      SELECT_TICKETS,
		apiLoading: true
	});

	timeTracker.start(LOADING_TIME_TICKET_TYPES);

	mxApi.getTicketTypes(exhibitorId, screeningMxId, authToken).then((data) => {
		timeTracker.end(LOADING_TIME_TICKET_TYPES);
		if (ticketing.sessionId !== _sessionId) return;

		let ticketTypes;
		let maxTickets;
		if (isObject(data) && data.ticketTypes) {
			({ ticketTypes, maxTickets } = data);
		} else {
			ticketTypes = data;
		}

		if (ticketTypes.length === 0) {
			goToError(getCopy('NO_TICKET_AVAIL_AT_THE_MOMENT'));
			return;
		}

		let updatedTicketing = Store.get().showtimes.ticketing;

		updatedTicketing = updatedTicketing.set({
			ticketTypes,
			apiLoading:  false
		});

		if (maxTickets) {
			updatedTicketing.set('maxTickets', maxTickets);
		}

		track({ a: 'ticketing-go-types' });

	}, (response) => {
		timeTracker.end(LOADING_TIME_TICKET_TYPES);
		if (ticketing.sessionId !== _sessionId) return;

		onHttpError(response);
	});
}

/**
 * Helper to generate a 41001 MX error.
 */
/* eslint-disable no-unused-vars */
const requestLotOfTicketsToKillMx = () => {
	const ticketing = Store.get().showtimes.ticketing;
	const { ticketTypes } = ticketing;
	if (!ticketTypes) return;
	const tickets = isObject(ticketing.tickets) ? ticketing.tickets.toJS() : {};

	ticketTypes.forEach((ticketType) => {
		tickets[ticketType.id] = {
			type:     ticketType.id,
			quantity: Math.round(573 + Math.random() * 1142)
		};
	});

	ticketing.set({ tickets });
};
/* eslint-enable no-unused-vars */

// if (typeof window !== 'undefined') {
// 	window.requestLotOfTicketsToKillMx = requestLotOfTicketsToKillMx;
// }

/**
 * Update the quantity for a certain ticket type inside the ticketing state.
 *
 * @param {Object} data - data received by the "UPDATE_TICKET_TYPE_QUANTITY" event
 * @param {string} data.ticketTypeId - id of the ticket type
 * @param {string} data.quantity - the new quantity of the ticket type
 */
function updateTicketTypeQuantity({ ticketTypeId, quantity }) {
	const ticketing = Store.get().showtimes.ticketing;
	const { ticketTypes } = ticketing;
	let { tickets } = ticketing;

	if (!ticketTypes) return;

	const ticketType = ticketing.ticketTypes.find((ticketType) => ticketType.id === ticketTypeId);

	if (!tickets) {
		tickets = ticketing.set('tickets', {}).tickets;
	}

	track({
		a: 'ticketing-change-type-quantity',
		l: ticketType.description
	});

	// Do we already have a ticket saved for that type
	if (tickets[ticketTypeId]) {
		if (quantity === 0) {
			tickets.remove(ticketTypeId);
		} else {
			tickets[ticketTypeId].set({ quantity });
		}

	} else {
		if (quantity === 0) return;

		tickets.set(ticketTypeId, {
			type:               ticketTypeId,
			quantity:           quantity,
			description:        ticketType.description,
			price:              ticketType.price,
			areaCategoryCode:   ticketType.areaCategoryCode,
			isAllocatedSeating: ticketType.isAllocatedSeating
		});
	}
}

/**
 * Return the total price of the order.
 *
 * @function
 * @param {Object=} ticketing - the ticketing state
 * @return {!string} the total price of the order, including the booking fee.
 */
export const getOrderTotalPrice = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const tickets = ticketing.tickets;
	let totalPrice = 0;

	for (const type in tickets) {
		const ticket = tickets[type];
		totalPrice += ticket.price * ticket.quantity;
	}

	totalPrice += ticketing.order.bookingFee;

	return totalPrice.toFixed(2);
};


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Ordering tickets

/**
 * Return whether 2 ticket sets are equals.
 *
 * @param {Object} tickets1 - the first ticket set
 * @param {Object} tickets2 - the second ticket set
 * @return {boolean} true if ticket sets are equals, false otherwise
 */
function areTicketsEqual(tickets1, tickets2) {
	const ticketIds = new Set();

	for (const ticketId in tickets1) {
		ticketIds.add(ticketId);
		if (!tickets2[ticketId]) return false;
	}

	for (const ticketId in tickets2) {
		ticketIds.add(ticketId);
		if (!tickets1[ticketId]) return false;
	}

	for (const ticketId of ticketIds) {
		const ticketType1 = tickets1[ticketId];
		const ticketType2 = tickets2[ticketId];

		if (ticketType1.areaCategoryCode !== ticketType2.areaCategoryCode) return false;
		if (ticketType1.quantity !== ticketType2.quantity) return false;
		if (ticketType1.price !== ticketType2.price) return false;
		if (ticketType1.description !== ticketType2.description) return false;
	}

	return true;
}

/**
 * Update the ticketing timeout if a new expiration time is received.
 *
 * @function
 * @param {number=} expiration - the number of minutes before a timeout
 * @param {Object=} ticketing - the ticketing state
 * @return {!Object} the new ticketing state
 */
const refreshTimeout = (expiration, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const expirationSet = typeof expiration === 'number';
	const timeoutSet = ticketing.timeout && ticketing.allowedTime;

	if (expirationSet || !timeoutSet) {
		const allowedTime = expirationSet ? expiration * 1e-3 : DEFAULT_BOOKING_TIMEOUT_S;
		ticketing = ticketing.set({
			timeout: Date.now() + allowedTime * 1e3,
			allowedTime: Math.round(allowedTime)
		});
	}

	return ticketing;
};

/**
 * Create a new order from the tickets stored in the ticketing state
 * or update the tickets of a previously created order with the new tickets.
 */
function createOrUpdateOrder() {
	const ticketing = Store.get().showtimes.ticketing;

	const tickets = ticketing.tickets.toJS(); // the ordered tickets
	const prevOrder = ticketing.order; // the previously created order, if any

	// You can't create an order without any ticket
	if (!tickets) {
		if (ticketing.state !== SELECT_TICKETS && ticketing.state !== NOT_ACTIVE) {
			Store.emit(actions.GO_TICKETING_TYPES);
		}
		return;
	}

	if (prevOrder) {
		const prevTickets = prevOrder.tickets.toJS();

		if (areTicketsEqual(tickets, prevTickets)) {
			goToNextSectionAfterOrderCreated(ticketing);
		} else {
			updateOrder(tickets, prevTickets);
		}
	} else {
		createOrder();
	}
}

/**
 * Function called before the seating map gets added to the store.
 * You can add any preprocessing here. The seat layout is modified in-place.
 *
 * @function
 * @param {!SeatLayout} seatLayout - the seat layout to process
 */
function prepareSeatingMap(seatLayout) {
	seatLayout.areas.forEach((area) => area.rows.forEach((row) => row.seats.forEach((seat) => {
		seat.areaCategoryCode = area.areaCategoryCode;
		seat.rowLabel = row.rowLabel;
	})));
}

/**
 * Create a new ticket order then go to the next step.
 * All required info are taken from the ticketing state.
 */
function createOrder() {
	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;

	ticketing.set({ apiLoading: true });

	timeTracker.start(LOADING_TIME_CREATE_ORDER);

	const { exhibitorId, theaterMxId, screeningMxId, seatingSupported } = ticketing;
	const tickets = ticketing.tickets.toJS();

	mxApi.createOrder(exhibitorId, theaterMxId, screeningMxId, seatingSupported,
		tickets, authToken).then((order) => {

		timeTracker.end(LOADING_TIME_CREATE_ORDER);
		if (ticketing.sessionId !== _sessionId) return;

		const { seatingMap, selectedSeats, groupedTickets, expiration } = order;

		// Lets store which tickets we have now, so we can easily compare when they get updated
		order.tickets = ticketing.tickets.toJS();

		// Delete the seating map from the order, so we don't add it inside the store twice
		delete order.seatingMap;

		ticketing = Store.get().showtimes.ticketing;
		ticketing = ticketing.set({
			order:      order,
			apiLoading: false
		});

		ticketing = refreshTimeout(expiration, ticketing);

		if (seatingSupported) {
			prepareSeatingMap(seatingMap);

			const pickedSeats = getPickedSeats(seatingMap);

			ticketing = ticketing.set({
				seating:     seatingMap,
				pickedSeats: pickedSeats,
				seats:       getSeats(selectedSeats, groupedTickets, ticketing.ticketTypes)
			});
			ticketing.order.set('pickedSeats', pickedSeats);
		}

		goToNextSectionAfterOrderCreated(ticketing);
	}, (response) => {
		timeTracker.end(LOADING_TIME_CREATE_ORDER);
		if (ticketing.sessionId !== _sessionId) return;

		onHttpError(response);
	});
}

/**
 * Update a previously created order.
 *
 * @param {Object} nextTickets - the newly ordered tickets
 * @param {Object} prevTickets - the previously ordered tickets
 */
function updateOrder(nextTickets, prevTickets) {
	// (1) let's find which ticket was added and which ticket was removed
	const addedTickets = {};
	const removedTickets = {};

	for (const ticketTypeId in nextTickets) {
		if (!prevTickets[ticketTypeId]) {
			addedTickets[ticketTypeId] = nextTickets[ticketTypeId].quantity;
		} else {
			const diff = nextTickets[ticketTypeId].quantity - prevTickets[ticketTypeId].quantity;
			if (diff > 0) {
				addedTickets[ticketTypeId] = diff;
			} else if (diff < 0) {
				removedTickets[ticketTypeId] = -diff;
			}
		}
	}
	for (const ticketTypeId in prevTickets) {
		if (!nextTickets[ticketTypeId]) {
			removedTickets[ticketTypeId] = prevTickets[ticketTypeId].quantity;
		}
	}

	// (2) prepare the request parameters
	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;
	const groupedTickets = ticketing.order.groupedTickets;

	const ticketsToAdd = [];
	for (const ticketTypeId in addedTickets) {
		ticketsToAdd.push({
			type:     ticketTypeId,
			quantity: addedTickets[ticketTypeId]
		});
	}

	const ticketIdsToRemove = [];
	for (const ticketTypeId in removedTickets) {
		const quantity = removedTickets[ticketTypeId];

		const previouslyOrderedTickets = groupedTickets.find(groupedTicket => {
			return groupedTicket.ticketTypeId === ticketTypeId;
		});

		if (!previouslyOrderedTickets) {
			console.error(`${quantity} tickets of type ${ticketTypeId} were removed, but could not be ` +
				'found among the previously ordered tickets:', previouslyOrderedTickets);
			continue;
		}

		const previousQuantity = previouslyOrderedTickets.ticketIds.length;

		for (let i = previousQuantity - quantity; i < previousQuantity; i++) {
			ticketIdsToRemove.push(previouslyOrderedTickets.ticketIds[i]);
		}
	}

	if (ticketsToAdd.length === 0 && ticketIdsToRemove.length === 0) {
		goToNextSectionAfterOrderCreated(ticketing);
		return;
	}

	// (3) send the needed requests
	const { exhibitorId, screeningMxId, seatingSupported } = ticketing;
	const orderId = ticketing.order.orderId;

	ticketing = ticketing.set('apiLoading', true);

	timeTracker.start(LOADING_TIME_UPDATE_ORDER);

	// const needSeating = seatingSupported && getTicketWithSeatCount(ticketing) > 0;

	mxApi.updateTickets(exhibitorId, screeningMxId, orderId, seatingSupported, ticketsToAdd,
		ticketIdsToRemove, authToken).then((updatedOrder) => {

		timeTracker.end(LOADING_TIME_UPDATE_ORDER);
		if (ticketing.sessionId !== _sessionId) return;

		const { seatingMap, selectedSeats, groupedTickets, expiration } = updatedOrder;

		// (4) update the order with the selectedSeats and the new tickets
		ticketing = Store.get().showtimes.ticketing;
		ticketing = ticketing.set('apiLoading', false);

		ticketing = refreshTimeout(expiration, ticketing);

		ticketing.order.set({
			price:          updatedOrder.price,
			bookingFee:     updatedOrder.bookingFee,
			groupedTickets: updatedOrder.groupedTickets,
			selectedSeats:  updatedOrder.selectedSeats,
			tickets:        nextTickets,
		});
		ticketing = Store.get().showtimes.ticketing;

		if (ticketing.seatingSupported) {
			prepareSeatingMap(seatingMap);

			const pickedSeats = getPickedSeats(seatingMap);

			ticketing = ticketing.set({
				seating:     seatingMap,
				pickedSeats: pickedSeats,
				seats:       getSeats(selectedSeats, groupedTickets, ticketing.ticketTypes)
			});
			ticketing.order.set('pickedSeats', pickedSeats);
		}

		// (5) display the next section
		goToNextSectionAfterOrderCreated(ticketing);
	}, (response) => {
		timeTracker.end(LOADING_TIME_UPDATE_ORDER);
		if (ticketing.sessionId !== _sessionId) return;

		onHttpError(response);
	});
}

/**
 * Return the number of ticket selected by the user.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {!number} the number of ticket purchased by the user
 */
const getTicketCount = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;
	const tickets = ticketing.tickets.toJS();

	let ticketCount = 0;
	for (const ticketType in tickets) {
		ticketCount += tickets[ticketType].quantity;
	}

	return ticketCount;
};

/**
 * Return the number of seat selectable by the user.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {!number} the numbe of seat selectable by the user
 */
const getTicketWithSeatCount = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;
	const tickets = ticketing.tickets.toJS();

	let seatCount = 0;
	for (const ticketType in tickets) {
		if (tickets[ticketType].isAllocatedSeating) {
			seatCount += tickets[ticketType].quantity;
		}
	}

	return seatCount;
};

/**
 * Go to the next step once the order is ready.
 * If the "SELECT_TICKETS" pane is displayed, this function will redirect:
 * <ul>
 *   <li>to the "SEATING" pane if the screening supports seating</li>
 *   <li>to the "REVIEW" pane otherwise</li>
 * </ul>
 *
 * @param {Object=} ticketing - the ticketing state
 */
function goToNextSectionAfterOrderCreated(ticketing) {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	if (ticketing.state === SELECT_TICKETS) {
		Store.emit(ticketing.seatingSupported && getTicketWithSeatCount(ticketing) > 0 ?
			actions.GO_TICKETING_SEATING : actions.GO_TICKETING_REVIEW);
	}
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Seating

/**
 * Return the normalized position of the given seat for building heatmaps.
 * The point (0.0, 0.0) is the top-left corner.
 *
 * @param {!Seat} seat - the input seat
 * @param {!SeatLayout} seatLayout - the seat layout including the seat
 * @param {SeatBoundingBox=} boundingBox - optional bounding box to use to get the normalized position
 * @return {{x: number, y: number}} the seat position, normalized between 0.0 and 1.0, or null in case of error
 */
export const getSeatPositionNormalized = (seat, seatLayout, boundingBox) => {
	const { position } = seat;
	const { areas } = seatLayout;
	if (!position || !areas) return null;

	const { areaNumber, columnIndex, rowIndex } = position;

	const area = areas.find(({ number }) => number === areaNumber);
	if (!area) return null;

	let x = area.left + (columnIndex + 0.5) / area.columnCount * area.width;
	let y = area.top + (rowIndex + 0.5) / area.rowCount * area.height;

	// Get bounding box parameters
	const left   = boundingBox ? boundingBox.left : seatLayout.boundaryLeft;
	const right  = boundingBox ? boundingBox.right : seatLayout.boundaryRight;
	const top    = boundingBox ? boundingBox.top : seatLayout.boundaryTop;
	const bottom = boundingBox ? boundingBox.bottom : 100.0;
	const width  = right - left;
	const height = bottom - top;

	// Normalize the position using the bounding box
	x = (x - left) / width;
	y = (y - top) / height;

	// MX seating map is rotated 180 deg, let's put the screen on the top
	x = 1.0 - x;
	y = 1.0 - y;

	return { x, y };
};

/**
 * Return the theater's occupancy based on the given seat layout.
 * The occupancy does not include the user's seats.
 *
 * @param {!SeatLayout} seatLayout - the seating layout, as sent by MX
 * @return {!number} the theater's occupancy, in [0.0, 1.0[, at the time of the booking
 */
export const getTheaterOccupancy = (seatLayout) => {
	let seatCount = 0;
	let soldSeatCount = 0;

	seatLayout.areas.forEach((area) => area.rows.forEach((row) => row.seats.forEach((seat) => {
		if (seat.status === seatStatus.SOLD) {
			soldSeatCount++;
		}
		seatCount++;
	})));

	if (seatCount === 0) return 1.0;
	return soldSeatCount / seatCount;
};

/**
 * Display the "SEATING" pane.
 * This function will call "CREATE_OR_UPDATE_ORDER" if there's currently no order.
 */
function goToTicketingSeating() {
	let ticketing = Store.get().showtimes.ticketing;

	track({ a: 'ticketing-go-seating' });

	ticketing = ticketing.set({ state: SELECT_SEATS });

	if (!ticketing.order) {
		Store.emit(actions.CREATE_OR_UPDATE_ORDER);
	}
}

/**
 * Make a request to reload the seat layout.
 * Calling this function will erase the currently picked seats.
 */
function flushSeatLayout() {
	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;
	if (!ticketing.order) return;

	const { exhibitorId, screeningMxId, order } = ticketing;

	ticketing.set('apiLoading', true);

	timeTracker.start(LOADING_TIME_FLUSH_LAYOUT);

	mxApi.getSeatingMap(exhibitorId, screeningMxId, order.orderId, authToken).then((result) => {
		timeTracker.end(LOADING_TIME_FLUSH_LAYOUT);
		if (ticketing.sessionId !== _sessionId) return;

		const { seatingMap } = result;

		prepareSeatingMap(seatingMap);

		const pickedSeats = getPickedSeats(seatingMap);

		ticketing = Store.get().showtimes.ticketing;
		ticketing = ticketing.set({
			seating:     seatingMap,
			pickedSeats: pickedSeats,
			apiLoading:  false
		});
		ticketing.order.set('pickedSeats', pickedSeats);
	}, (response) => {
		timeTracker.end(LOADING_TIME_FLUSH_LAYOUT);
		if (ticketing.sessionId !== _sessionId) return;

		onHttpError(response);
	});
}

/**
 * Pick seats from the seating map or unpick them if one of them is already picked.
 *
 * @param {Object} data - data received by the "PICK_OR_UNPICK_SEATS" event
 * @property {Array.<Seat>|Seat} data.seats - array of seats to pick or unpick from the seating map
 * @return {boolean} true if the action succeed, false otherwise
 */
function pickOrUnpickSeats(data) {
	let { seats } = data;
	if (!seats) return;
	if (!Array.isArray(seats)) {
		seats = [seats];
	}

	const ticketing = Store.get().showtimes.ticketing;
	const pickedSeats = ticketing.pickedSeats;

	const alreadyPickedSeat = seats.find((seat) => {
		return pickedSeats.find((pickedSeat) => areSeatsEqual(pickedSeat.seat, seat));
	});

	if (alreadyPickedSeat) {
		return unpickSeats(seats);
	}
	return pickSeats(seats);
}

/**
 * Pick seats from the seating map.
 * This function automatically chooses which seat to move when the user does not have any more
 * ticket available.
 * If the number of seats to pick exceed the number of ticket available for the specific area,
 * an error is displayed to the user.
 *
 * @param {Array.<Seat>} seats - an array of seats to pick from the seating map
 * @return {boolean} true if the action succeed, false otherwise
 */
function pickSeats(seats) {
	let ticketing = Store.get().showtimes.ticketing;

	// First, check that the action is valid, then pick the seat
	// so we don't have to reverse the operation in case of failure
	const { areas, areaCategories } = ticketing.seating;

	const availablePerArea = {};
	areaCategories.forEach((areaCategory) => {
		availablePerArea[areaCategory.areaCategoryCode] = areaCategory.seatsToAllocate;
	});

	const pickedPerArea = {};
	seats.forEach((seat) => {
		const { areaCategoryCode } = seat;

		if (pickedPerArea[areaCategoryCode]) {
			pickedPerArea[areaCategoryCode] += 1;
		} else {
			pickedPerArea[areaCategoryCode] = 1;
		}
	});

	for (const areaCategoryCode in pickedPerArea) {
		const areaCategory = areaCategories.find((area) => area.areaCategoryCode === areaCategoryCode);

		const availCount = areaCategory ? areaCategory.seatsToAllocate : 0;
		const pickedCount = pickedPerArea[areaCategoryCode];

		if (pickedCount > availCount) {
			let errorMessage;

			if (areas.length > 1) {
				const area = areas.find((area) => area.areaCategoryCode === areaCategoryCode);
				const areaName = area && area.description || areaCategoryCode;

				if (availCount > 0) {
					errorMessage = getCopy('ONLY_N_TICKETS_AVAIL_AREA', {
						NUM_AVAIL: availCount,
						AREA_NAME: areaName
					});
				} else {
					errorMessage = getCopy('NO_TICKET_AVAIL_AREA', { AREA_NAME: areaName });
				}
			} else {
				if (availCount > 0) {
					errorMessage = getCopy('ONLY_N_TICKETS_AVAIL', { NUM_AVAIL: availCount });
				} else {
					// This message should never appear in theory
					errorMessage = getCopy('NO_TICKET_AVAIL');
				}
			}
			ticketing.set('apiMessage', errorMessage);

			return false;
		}
	}

	// Now we can safely pick the seats
	const pickedSeats = ticketing.pickedSeats.toJS(); // we are gonna modify this one

	const alreadyPickedPerArea = {};
	pickedSeats.forEach((pickedSeat) => {
		const areaCategoryCode = pickedSeat.seat.areaCategoryCode;
		if (alreadyPickedPerArea[areaCategoryCode]) {
			alreadyPickedPerArea[areaCategoryCode] += 1;
		} else {
			alreadyPickedPerArea[areaCategoryCode] = 1;
		}
	});

	// Sometimes companion seats are not paired with wheelchair space
	// If such a seat is picked, we need to send a disclaimer to the user.
	// Since this disclaimer doesn't prevent the user action, the message is sent at the very end.
	const loneCompanionSeats = [];

	for (let i = 0; i < seats.length; i++) {
		const seat = seats[i];

		const isCompanion = isCompanionSeat(seat);
		let wheelchairSeat = null;

		// If the user picks a companion seat, the corresponding wheelchair should be picked as well
		if (isCompanion) {
			wheelchairSeat = findWheelchairInGroup(ticketing.seating, seat.seatsInGroup);
			if (wheelchairSeat) {
				// Seek the wheelchair among the already picked seats and the newly picked seats
				let pickedWheelchair = pickedSeats.find((pickedSeat) => {
					return areSeatsEqual(pickedSeat.seat, wheelchairSeat);
				});

				if (!pickedWheelchair) {
					pickedWheelchair = seats.find((seat) => {
						areSeatsEqual(seat, wheelchairSeat);
					});

					if (!pickedWheelchair) {
						const companionLabel = seat.rowLabel + seat.seatLabel;
						const wheelchairLabel = wheelchairSeat.rowLabel + wheelchairSeat.seatLabel;

						ticketing.set('apiMessage', getCopy('PICK_THE_WHEELCHAIR', {
							COMP_LABEL: companionLabel,
							WLCHR_LABEL: wheelchairLabel
						}));
						return false;
					}
				}
			} else {
				const companionLabel = seat.rowLabel + seat.seatLabel;
				loneCompanionSeats.push(companionLabel);
				console.warn('updateTicketing.js: pickSeats(): No wheelchair found in the group of the ' +
					`companion seat ${companionLabel}:`, seat.seatsInGroup);
			}
		}

		const { areaCategoryCode } = seat;

		// We need to know whether there are unpicked seats
		// otherwise we will start moving seats that were picked already
		const availableCount     = availablePerArea[areaCategoryCode] || 0;
		const alreadyPickedCount = alreadyPickedPerArea[areaCategoryCode] || 0;
		const unpickedCount      = availableCount - alreadyPickedCount;

		if (unpickedCount > 0) {
			pickedSeats.push({
				seat:       seat,
				lastAction: Date.now()
			});
			alreadyPickedPerArea[areaCategoryCode] += 1;
		} else {
			// Pick the oldest seat among the picked one:
			let seatToMove = null;
			let oldestAction = Number.MAX_SAFE_INTEGER;

			pickedSeats.forEach((pickedSeat) => {
				if (pickedSeat.seat.areaCategoryCode === areaCategoryCode &&
					pickedSeat.lastAction < oldestAction) {
					seatToMove = pickedSeat;
					oldestAction = pickedSeat.lastAction;
				}
			});

			if (isCompanion && wheelchairSeat) {
				if (areSeatsEqual(seatToMove.seat, wheelchairSeat)) {
					// Trying to move a wheelchair to one of the companion seats in its group
					ticketing.set('apiMessage', getCopy('NOT_ENOUGH_TICKET_FOR_COMPANION'));
					return false;
				}
			}

			if (seatToMove.seat.seatsInGroup.length > 0) {
				seatToMove.seat.seatsInGroup.forEach((seatPosition) => {
					const indexToRemove = pickedSeats.findIndex((pickedSeat) => {
						return areSeatPositionsEqual(pickedSeat.seat.position, seatPosition);
					});
					if (indexToRemove > -1) {
						pickedSeats.splice(indexToRemove, 1);
					}
					alreadyPickedPerArea[areaCategoryCode] -= 1;
				});

				pickedSeats.push({
					seat: seat,
					lastAction: Date.now()
				});
				alreadyPickedPerArea[areaCategoryCode] += 1;
			} else {
				seatToMove.seat = seat;
				seatToMove.lastAction = Date.now();
			}
		}
	}

	// If some lone companion seats are picked, show a disclaimer
	if (loneCompanionSeats.length > 0) {
		const copy = loneCompanionSeats.length > 1 ? 'SEATS_ARE_COMPANION' : 'SEAT_IS_COMPANION';
		ticketing = ticketing.set('apiMessage', getCopy(copy, {
			LABEL: loneCompanionSeats.join(', ')
		}));
	}

	ticketing.set('pickedSeats', pickedSeats);

	return true;
}

/**
 * Unpick seats from the seating map.
 *
 * @param {Array.<Seat>} seats - an array of seats to unpick
 * @return {boolean} true if at least one seat was unpicked, false otherwise
 */
function unpickSeats(seats) {
	const ticketing = Store.get().showtimes.ticketing;
	const pickedSeats = ticketing.pickedSeats.toJS();

	let modified = false;
	seats.forEach((seat) => {
		// If a wheelchair is unpicked, unpick all its companion seats too
		if (isWheelchair(seat) && seat.seatsInGroup.length > 0) {
			seat.seatsInGroup.forEach((seatPos) => {
				const index = pickedSeats.findIndex((pickedSeat) => {
					return areSeatPositionsEqual(pickedSeat.seat.position, seatPos);
				});
				if (index > -1) {
					pickedSeats.splice(index, 1);
					modified = true;
				}
			});
		} else {
			const index = pickedSeats.findIndex((pickedSeat) => areSeatsEqual(pickedSeat.seat, seat));
			if (index > -1) {
				pickedSeats.splice(index, 1);
				modified = true;
			}
		}
	});

	if (modified) {
		ticketing.set('pickedSeats', pickedSeats);
	}

	return modified;
}

/**
 * Return whether seats should be updated on the server.
 *
 * @return {boolean} true if seats should be update, false otherwise
 */
function shouldUpdateSeats() {
	const ticketing = Store.get().showtimes.ticketing;

	if (!Object.prototype.hasOwnProperty.call(ticketing, 'order')) return false;
	if (!Object.prototype.hasOwnProperty.call(ticketing, 'pickedSeats')) return false;
	if (!Object.prototype.hasOwnProperty.call(ticketing.order, 'pickedSeats')) return true;

	const pickedSeats = ticketing.pickedSeats;
	const cachedPickedSeats = ticketing.order.pickedSeats;

	if (pickedSeats.length !== getTicketWithSeatCount(ticketing)) return true;
	if (pickedSeats.length === 0) return true;

	// return true when no matching seat is found
	return !!pickedSeats.find((pickedSeat) => {
		return !cachedPickedSeats.find((cachedSeat) => {
			return areSeatPositionsEqual(pickedSeat.seat.position, cachedSeat.seat.position);
		});
	});
}

/**
 * Return the number of seats to pick before updating the order.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {!number} the number of seats not picked yet, or null if no order is created
 */
const getNumberOfSeatToPick = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const availableSeats = getTicketWithSeatCount(ticketing);
	const alreadyPickedSeat = ticketing.pickedSeats.length;

	return availableSeats - alreadyPickedSeat;
};

/**
 * Return an array of seat slots.
 * Each slot contains a seats picked on the map or null if the slot is empty.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {Array.<Seat>} - an array of seat slots
 */
export const getSeatSlots = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const seatSlots = [];

	if (!Object.prototype.hasOwnProperty.call(ticketing, 'pickedSeats')) return seatSlots;
	if (!Object.prototype.hasOwnProperty.call(ticketing, 'tickets')) return seatSlots;

	const pickedSeats = ticketing.pickedSeats;
	const tickets = ticketing.tickets.toJS();

	let availableSeats = 0;
	for (const ticketId in tickets) {
		if (tickets[ticketId].isAllocatedSeating) {
			availableSeats += tickets[ticketId].quantity;
		}
	}

	seatSlots.length = availableSeats;
	seatSlots.fill(null);

	for (let i = 0; i < pickedSeats.length; i++) {
		seatSlots[i] = pickedSeats[i].seat;
	}

	return seatSlots;
};

/**
 * Return whether a seat is available.
 *
 * @param {Seat} seat - the seat to test
 * @param {Array.<PickedSeat>} pickedSeats - the array of currently picked seats
 * @return {boolean} true if the seat is avaiable, false otherwise
 */
const isSeatAvailable = (seat, pickedSeats) => {
	if (seat.status === seatStatus.SOLD) return false;
	for (let i = 0; i < pickedSeats.length; i++) {
		if (areSeatsEqual(pickedSeats[i].seat, seat)) return false;
	}
	return true;
};

/**
 * Return the row of a specific seat in the seating map.
 *
 * @function
 * @param {SeatLayout} seatingLayout - the seating layout containing the row to get
 * @param {Seat} seat - the seat in the row to get
 * @return {Row} the row having the given seat, null if not found
 */
const getRowForSeat = (seatLayout, seat) => {
	const { position } = seat;

	const area = seatLayout.areas.find(({ number }) => number === position.areaNumber);
	if (!area) return null;

	const { rows } = area;
	const row = rows[position.rowIndex];
	if (row && row.rowLabel === seat.rowLabel) return row;

	return rows.find(({ rowLabel }) => rowLabel === seat.rowLabel) || null;
};

/**
 * Return whether the orphan seat policy is respected.
 * Display a message if it's not.
 *
 * @param {SeatLayout} seatLayout - the seat layout, as defined in the MX API
 * @param {Array.<PickedSeat>} pickedSeats - an array of seats picked from the map
 * @return {boolean} true if the orphan seat policy is respected, false otherwise
 */
const checkOrphanSeatPolicy = (seatLayout, pickedSeats) => {
	const orphanSeats = [];

	for (const { seat } of pickedSeats) {
		if (seat.seatsInGroup.length > 1 || isSeatForDisablePeople(seat)) continue;
		// NOTE: ^^^ No need to check orphan seats with sofas or wheelchairs
		//           We're explicitly checking for wheelchair and companion seats because we found
		//           some cases where those seats are not wrapped in groups (19-04-18, Envision)

		const { position } = seat;

		const row = getRowForSeat(seatLayout, seat);
		if (!row) {
			console.error(`Orphan seats: Row ${seat.rowLabel} not found in map:`, seatLayout);
			return false;
		}

		const { seats } = row;
		const { columnIndex } = position;

		// Check the left-hand side
		const leftSeat = seats.find((seat) => seat.position.columnIndex === columnIndex - 1);
		if (leftSeat && isSeatAvailable(leftSeat, pickedSeats)) {
			const leftLeftSeat = seats.find((seat) => seat.position.columnIndex === columnIndex - 2);
			if (!leftLeftSeat || !isSeatAvailable(leftLeftSeat, pickedSeats)) {
				const orphanSeatLabel = leftSeat.rowLabel + leftSeat.seatLabel;
				if (orphanSeats.indexOf(orphanSeatLabel) === -1) orphanSeats.push(orphanSeatLabel);
			}
		}

		// Check the right-hand side
		const rightSeat = seats.find((seat) => seat.position.columnIndex === columnIndex + 1);
		if (rightSeat && isSeatAvailable(rightSeat, pickedSeats)) {
			const rightRightSeat = seats.find((seat) => seat.position.columnIndex === columnIndex + 2);
			if (!rightRightSeat || !isSeatAvailable(rightRightSeat, pickedSeats)) {
				const orphanSeatLabel = rightSeat.rowLabel + rightSeat.seatLabel;
				if (orphanSeats.indexOf(orphanSeatLabel) === -1) orphanSeats.push(orphanSeatLabel);
			}
		}
	}

	if (orphanSeats.length > 0) {
		const ticketing = Store.get().showtimes.ticketing;
		ticketing.set('apiMessage', getCopy('CANNOT_LEAVE_ORPHAN_SEATS', {
			ORPHAN_SEATS: orphanSeats.join(', ')
		}));
		return false;
	}

	return true;
};

/**
 * Send the currently picked seats to the server.
 * This function will bring the user to the review pane, if no error occurred.
 */
function updateTicketingSeats() {
	// Check that order has changed
	if (!shouldUpdateSeats()) {
		goToTicketingReview();
		return;
	}

	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;
	const pickedSeats = ticketing.pickedSeats.toJS();

	// Check that they are no remaining seat
	const numberOfSeatsToPick = getNumberOfSeatToPick(ticketing);
	if (numberOfSeatsToPick > 0) {
		ticketing.set('apiMessage', getCopy('STILL_N_SEATS_TO_PICK', {
			NUM_SEAT: numberOfSeatsToPick
		}));
		return;
	}

	// Check the orphan seat policy if needed
	const orphanSeatPolicyEnabled = true;
	// TODO: ^^^ Starlight-specific policy, please move this somewhere
	if (orphanSeatPolicyEnabled && pickedSeats.length === getTicketWithSeatCount(ticketing)) {
		//                           ^^^ Not sure about the purpose of this condition?
		if (!checkOrphanSeatPolicy(ticketing.seating, pickedSeats)) return;
	}

	// Tracking of seat movements
	const seatsBefore = ticketing.order.pickedSeats.map(({ seat }) => seat.rowLabel + seat.seatLabel);
	const seatsAfter = ticketing.pickedSeats.map(({ seat }) => seat.rowLabel + seat.seatLabel);
	const seatMovement = seatsBefore + '=>' + seatsAfter;

	track({
		a: 'ticketing-seat-move',
		l: seatMovement
	});

	const { exhibitorId, screeningMxId, order } = ticketing;

	// Create the new array of seats
	const seats = pickedSeats.map(({ seat }) => ({
		areaCategoryCode: seat.areaCategoryCode,
		areaNumber:       seat.position.areaNumber,
		columnId:         seat.position.columnIndex,
		rowId:            seat.position.rowIndex
	}));

	ticketing.set('apiLoading', true);

	timeTracker.start(LOADING_TIME_UPDATE_SEATS);

	mxApi.updateSeats(exhibitorId, screeningMxId, order.orderId, seats, authToken).then((order) => {
		if (ticketing.sessionId !== _sessionId) return;

		mxApi.getSeatingMap(exhibitorId, screeningMxId, order.orderId, authToken).then((result) => {
			timeTracker.end(LOADING_TIME_UPDATE_SEATS);
			if (ticketing.sessionId !== _sessionId) return;

			const { seatingMap } = result;
			const { selectedSeats, groupedTickets, price, bookingFee } = order;

			prepareSeatingMap(seatingMap);

			const pickedSeats = getPickedSeats(seatingMap);

			ticketing = Store.get().showtimes.ticketing;
			ticketing = ticketing.set({
				seating:     seatingMap,
				pickedSeats: pickedSeats,
				seats:       getSeats(selectedSeats, groupedTickets, ticketing.ticketTypes),
				apiLoading:  false
			});
			ticketing.order.set({
				price:          price,
				bookingFee:     bookingFee,
				groupedTickets: groupedTickets,
				selectedSeats:  selectedSeats,
				pickedSeats:    pickedSeats
			});

			goToTicketingReview();
		}, (response) => {
			timeTracker.end(LOADING_TIME_UPDATE_SEATS);
			if (ticketing.sessionId !== _sessionId) return;

			onHttpError(response);
		});

	}, (response) => {
		timeTracker.end(LOADING_TIME_UPDATE_SEATS);
		if (ticketing.sessionId !== _sessionId) return;

		// Handle MX Error 41001: Somebody has taken some of those seats before the user
		if (isMxErrorObject(response)) {
			const { errorCode } = response.data.error;
			if (errorCode === mxErrors.SEATS_UNAVAILABLE) {
				track({ a: 'ticketing-seats-race' });
				ticketing = Store.get().showtimes.ticketing;
				ticketing.set({
					apiLoading: false,
					apiMessage: getCopy('THOSE_SEATS_WERE_RESERVED_RECENTLY'),
					apiMessageCallback: () => Store.emit(actions.FLUSH_SEAT_LAYOUT)
				});
				return;
			}
		}

		onHttpError(response);
	});
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Ticketing review

/**
 * Display the review pane.
 */
function goToTicketingReview() {
	track({ a: 'ticketing-go-review' });
	Store.get().showtimes.ticketing.set('state', REVIEW);
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Contact details

/**
 * Display the contact details pane.
 * Required details may be different from one payment gateway to another.
 */
function goToTicketingContactDetails() {
	track({ a: 'ticketing-go-contact-details' });
	Store.get().showtimes.ticketing.set('state', CONTACT_DETAILS);
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Payments

/**
 * Check that the iframe communicator is correctly working before going to the payment section.
 *
 * @param {Object} data - data received by the "GO_TICKETING_PAYMENT" event
 */
function goToTicketingPaymentPreCheck(data) {
	const ticketing = Store.get().showtimes.ticketing;

	const config = gatewayConfigs[ticketing.paymentGateway] || {};
	if (config.useIframeComm) {
		ticketing.set('apiLoading', true);

		timeTracker.start(LOADING_TIME_PAYMENT_PRECHECK);

		checkIframeCommunicatorUrl().then(() => {
			timeTracker.end(LOADING_TIME_PAYMENT_PRECHECK);

			// This check has already succeed and is not needed anymore
			Store.off(actions.GO_TICKETING_PAYMENT, goToTicketingPaymentPreCheck);
			Store.on(actions.GO_TICKETING_PAYMENT, goToTicketingPayment);

			if (ticketing.sessionId !== _sessionId) return;
			goToTicketingPayment(data);
		}, () => {
			track({ a: 'ticketing-error-iframe-comm' });
			goToError();
		});
	} else {
		goToTicketingPayment(data);
	}
}

/**
 * Display the payment pane.
 * Note that contact details should be valid for the user to be redirected to the payment section.
 *
 * @param {Object} contactDetails - the details provided by the user to prepare the transaction
 */
function goToTicketingPayment(contactDetails) {
	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;

	// Get contact details
	const { exhibitorId, theaterMxId } = ticketing;
	const { orderId } = ticketing.order;
	const paymentArgs = {};
	const { email, name, phone } = contactDetails;
	const iframeCommunicatorUrl = getIframeCommunicatorUrl();

	let { loyaltyNumber } = contactDetails;
	if (!ticketing.loyaltyEnabled) loyaltyNumber = undefined;

	switch (ticketing.paymentGateway) {
		case gateways.AUTHNET:
			paymentArgs.iFramecommunicationsChannel = iframeCommunicatorUrl;
			paymentArgs.returnUrl = 'https://httpbin.org/get';
			// NOTE: 'about:blank' is giving errors here ^^^
			paymentArgs.cancelUrl = window.location.href.replace(/&/g, '%26');
			// NOTE: could there be issues with double tracking because we load in  the same page twice
			// Maybe it should redirect to the iframeCommunicator instead
			break;

		case gateways.VANTIV:
			paymentArgs.returnUrl = iframeCommunicatorUrl;
			break;

		case gateways.PXPAY:
			paymentArgs.returnUrl = iframeCommunicatorUrl + '?succeed';
			paymentArgs.cancelUrl = iframeCommunicatorUrl + '?cancelled';
			break;

		case gateways.VERIFONE2:
			paymentArgs.returnUrl = iframeCommunicatorUrl + '?readytocheck';
			break;

		case gateways.BRAINTREE:
			// NOTE: I don't think this is used
			paymentArgs.returnUrl = iframeCommunicatorUrl + '?braintree';
			break;
	}

	ticketing.set('apiLoading', true);

	timeTracker.start(LOADING_TIME_START_TRANSACTION);

	mxApi.startTransaction(exhibitorId, theaterMxId, orderId, paymentArgs, name, email, phone,
		loyaltyNumber, authToken).then((paymentData) => {
		timeTracker.end(LOADING_TIME_START_TRANSACTION);
		if (ticketing.sessionId !== _sessionId) return;

		track({ a: 'ticketing-go-payment' });

		ticketing = Store.get().showtimes.ticketing;
		ticketing = ticketing.set({
			apiLoading:  false,
			state:       PAYMENT,
			paymentData: paymentData,
			usedLoyalty: ticketing.loyaltyEnabled && loyaltyNumber !== undefined
		});
		ticketing = ticketing.remove('iframeSize');

		// NOTE: This is a trick to track Authorize.net response time
		timeTracker.start(LOADING_TIME_PAYMENTS);

	}, (response) => {
		timeTracker.end(LOADING_TIME_START_TRANSACTION);
		if (ticketing.sessionId !== _sessionId) return;

		if (isMxErrorObject(response)) {
			const { errorCode } = response.data.error;

			if (errorCode === mxErrors.CANT_CONNECT_TO_LOYALTY ||
				errorCode === mxErrors.LOYALTY_NOT_ALLOWED_FOR_CINEMA_CHAIN ||
				errorCode === mxErrors.INSUFFICIENT_LOYALTY_MEMBER_DETAILS ||
				errorCode === mxErrors.LOYALTY_MEMBER_NOT_FOUND) {
				track({
					a: 'ticketing-error-from-mx',
					l: errorCode
				});
				ticketing = Store.get().showtimes.ticketing;
				ticketing.set({
					apiLoading: false,
					apiMessage: getCopy('MX_' + getMxErrorKey(errorCode))
				});
				return;
			}
		}

		onHttpError(response);
	});
}

/**
 * Return the list of seats selected by the user.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {string} a list of tickets by area category, separated by a comma
 */
function getTicketList(ticketing) {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;
	const tickets = ticketing.tickets.toJS();

	const ticketList = [];

	for (const ticketType in tickets) {
		ticketList.push(`${ticketType}:${tickets[ticketType].quantity}`);
	}

	return ticketList.join(';');
}

/**
 * Return the elapsed time since the ticketing session started.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {number} the elapsed time (in seconds), or null if no session is active
 */
function getElapsedTime(ticketing) {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	let elapsedTime = null;

	if (typeof ticketing.startDate === 'number') {
		elapsedTime = Math.ceil((Date.now() - ticketing.startDate) * 1e-3);
	}

	return elapsedTime;
}

/**
 * Get the positions of user's picked seats.
 *
 * @param {Object} ticketing - the ticketing state
 * @return {Array.<Object>} array of 2D normalized positions
 */
const getSeatingPositions = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const seatLayout = ticketing.seating;
	const { pickedSeats } = ticketing;
	if (!seatLayout || !pickedSeats) return;

	const boundingBox = getSeatLayoutBoundingBox(seatLayout);

	const positions = [];

	pickedSeats.forEach((pickedSeat) => {
		const position = getSeatPositionNormalized(pickedSeat.seat, ticketing.seating, boundingBox);
		if (position) {
			positions.push({
				x: position.x.toFixed(HEATMAP_FLOAT_PRECISION),
				y: position.y.toFixed(HEATMAP_FLOAT_PRECISION)
			});
		}
	});

	return positions;
};

/**
 * @typedef {Object} OrderDetails
 * @property {number} sessionId - the session ID (which is also the number of ticketing sessions opened)
 * @property {string} exhibitorId - the chain ID
 * @property {string} theaterId - the theater ID
 * @property {string} screeningId - the screening ID
 * @property {string} orderId - the order ID
 * @property {number} price - the order price
 * @property {number} bookingFee - the order booking fee
 * @property {string} paymentGateway - the payment gateway used
 * @property {boolean} usedLoyalty - whether loyalty was used
 * @property {number} ticketCount - the number of tickets sold
 * @property {string} tickets - list of ticket(s) purchased
 * @property {string} seats - list of seat(s) reserved
 * @property {Array.<Object>} seatPositions - list of normalized seat positions
 * @property {Object} meta - extra details about the screening
 */

/**
 * Return the recap of the order.
 *
 * @param {Object=} ticketing - the ticketing state
 * @return {OrderDetails} the order recap
 */
const getOrderDetails = (ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const details = {
		sessionId:      ticketing.sessionId,
		exhibitorId:    ticketing.exhibitorId,
		theaterId:      ticketing.theaterMxId,
		screeningId:    ticketing.screeningMxId,
		orderId:        ticketing.order.orderId,
		price:          ticketing.order.price,
		bookingFee:     ticketing.order.bookingFee,
		paymentGateway: ticketing.paymentGateway,
		usedLoyalty:    ticketing.loyaltyEnabled && ticketing.usedLoyalty,
		ticketCount:    getTicketCount(ticketing),
		tickets:        getTicketsText(ticketing.tickets)
	};

	if (ticketing.seatingSupported) {
		details.seats = ticketing.seats.map(s => s.label);
		details.seatPositions = getSeatingPositions(ticketing);
	}

	details.meta = ticketing.meta;

	return details;
};

/**
 * Complete an order after the payment is done.
 *
 * @param {Object} paymentResponse - the response received from the payment gateway
 */
const completeOrderAfterPaymentSucceed = (paymentResponse) => {
	if (!isObject(paymentResponse)) goToError();

	track({ a: 'ticketing-payment-succeed-start-complete-order' });

	const store = Store.get();
	const { authToken } = store.user;
	let { ticketing } = store.showtimes;
	ticketing = ticketing.set({
		apiLoading:      true,
		transactionDone: true
	});

	timeTracker.start(LOADING_TIME_COMPLETE_ORDER);

	const { exhibitorId, theaterMxId } = ticketing;
	const { orderId } = ticketing.order;

	mxApi.completeOrder(exhibitorId, theaterMxId, orderId, paymentResponse, authToken)
		.then(({ code, bookingId }) => {
			timeTracker.end(LOADING_TIME_COMPLETE_ORDER);
			if (ticketing.sessionId !== _sessionId) return;

			ticketing = Store.get().showtimes.ticketing;

			const paymentSucceed = code === 'approved';

			ticketing = ticketing.set({
				state:      COMPLETE,
				apiLoading: false,
				payment:    { code, bookingId }
			});
			// TODO: ^ the payment object needs to be cleaned as well
			//         when the session is cleared

			track({ a: 'ticketing-go-complete' });

			if (paymentSucceed) {
				track({
					a: 'ticketing-purchase-complete',
					l: ticketing.sessionId
				});
				if (ticketing.loyaltyEnabled && ticketing.usedLoyalty) {
					track({ a: 'ticketing-purchase-with-loyalty' });
				}
				const elapsedTime = getElapsedTime(ticketing);
				if (elapsedTime) {
					track({
						a: 'ticketing-time-to-purchase',
						l: elapsedTime
					});
				}
				track({
					a: 'ticketing-purchase-theater',
					l: ticketing.theaterId
				});
				track({
					a: 'ticketing-purchase-gateway',
					l: ticketing.paymentGateway
				});
				track({
					a: 'ticketing-purchase-ticket-count',
					l: getTicketCount(ticketing)
				});
				track({
					a: 'ticketing-purchase-tickets',
					l: getTicketList(ticketing)
				});
				if (ticketing.seatingSupported) {
					track({
						a: 'ticketing-purchase-occupancy',
						l: getTheaterOccupancy(ticketing.seating)
					});
					const seatPositions = getSeatingPositions(ticketing);
					seatPositions.forEach((pos) => {
						track({ a: 'ticketing-purchase-heatmap', l: pos.x + ' ' + pos.y });
					});
				}
				try {
					const details = getOrderDetails(ticketing);
					Store.emit(actions.TICKET_PURCHASE_COMPLETE, details);
				} catch (err) {
					console.error(`Ticket purchase complete tracking failed: ${err}`);
				}
			}

			/* eslint-disable no-console */
			console.log('bookingId:', bookingId);
			console.log('orderId:', orderId);
			/* eslint-enable no-console */

			ticketing = ticketing.remove('timeout');
		}, (response) => {
			timeTracker.end(LOADING_TIME_COMPLETE_ORDER);
			if (ticketing.sessionId !== _sessionId) return;

			onHttpError(response);
		});
};

/**
 * Handle messages received from the Authorize.net iframe.
 *
 * @param {Object} data - the message data
 * @property {string} data.payload - the message sent from the iframe
 * @property {string} data.referrer - the referrer of the message
 * @param {Object=} ticketing - the ticketing state
 */
const handleAuthnetPaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = qs.parse(data.payload);
	if (!isObject(msgData)) return;

	const action = msgData.action;
	if (typeof action !== 'string') return;

	switch (action) {
		case AUTHNET_MSG_RESIZE_WINDOW:
			// Receiving ideal iframe dimensions
			const width = parseInt(msgData.width);
			const height = parseInt(msgData.height);
			// Note that this will be called every time the content
			// of the iframe is changing.
			ticketing = ticketing.set('iframeSize', { width, height });
			timeTracker.end(LOADING_TIME_PAYMENTS); // Authorize.Net specific?
			break;
		case AUTHNET_MSG_CANCEL:
			// If the user is waiting for the transaction to be confirmed,
			// the user shouldn't be able to cancel the payment.
			if (ticketing.transactionDone) return;
			// TODO: Go back one step
			// FIXME: MX Bug? this one is never called...
			goToTicketingContactDetails();
			break;
		case AUTHNET_MSG_TRANSACTION_RESPONSE:
			// --- IMPORTANT ---
			// At this point we know the payment succeed.
			// BUT we MUST NOT tell the user now because the user won't get the tickets
			// until MX confirmed the payment. So just show the loader.
			// -----------------
			try {
				const response = JSON.parse(msgData.response);
				completeOrderAfterPaymentSucceed(response);
			} catch (e) {
				goToError();
				// TODO: ^^^ Add a message?
				return;
			}
			break;
	}
};

/**
 * Handle messages received from the Vantiv iframe.
 *
 * @param {Object} data - the message data
 * @param {Object=} ticketing - the ticketing state
 */
const handleVantivPaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = qs.parse(data.payload);
	if (!isObject(msgData)) return;

	// The user cancelled the transaction
	if (msgData.HostedPaymentStatus === 'Cancelled') {
		goToTicketingContactDetails();
		return;
	}

	if (msgData.ExpressResponseMessage === 'Approved') {
		completeOrderAfterPaymentSucceed(msgData);
	} else {
		goToError();
		// TODO: ^^ Improve that section
	}
};

/**
 * Handle messages received from the Payment Express iframe.
 *
 * @param {Object} data - the message data
 * @param {Object=} ticketing - the ticketing state
 */
const handlePxPayPaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = qs.parse(data.payload);
	if (!isObject(msgData)) return;

	// The user cancelled the transaction
	if (Object.prototype.hasOwnProperty.call(msgData, 'succeed')) {
		completeOrderAfterPaymentSucceed(msgData);
	} else if (Object.prototype.hasOwnProperty.call(msgData, 'cancelled')) {
		goToTicketingContactDetails();
	} else {
		goToError();
		// TODO: ^^ Improve that section
	}
};

/**
 * Handle messages received from the Verifone PP2 iframe.
 *
 * @param {Object} data - the message data
 * @param {Object=} ticketing - the ticketing state
 */
const handleVerifone2PaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = qs.parse(data.payload);
	if (!isObject(msgData)) return;

	if (Object.prototype.hasOwnProperty.call(msgData, 'readytocheck')) {
		completeOrderAfterPaymentSucceed(msgData);
	}
};

/**
 * Handle messages received from the Braintree API.
 *
 * @param {Object} data - the message data
 * @param {Object=} ticketing - the ticketing state
 */
const handleBraintreePaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = data.payload; // already an object
	if (!isObject(msgData)) return;

	if (Object.prototype.hasOwnProperty.call(msgData, 'nonce')) {
		completeOrderAfterPaymentSucceed(msgData);
	}
};

/**
 * Handle messages received from the Mastercard Payment API.
 *
 * @param {Object} data - the message data
 * @param {Object=} ticketing - the ticketing state
 */
const handleMastercardPaymentMessage = (data, ticketing) => {
	if (!ticketing) ticketing = Store.get().showtimes.ticketing;

	const msgData = data.payload; // already an object
	if (!isObject(msgData)) return;

	completeOrderAfterPaymentSucceed(msgData);
};

/**
 * Called when a message is received from an iframe.
 *
 * NOTE: This function is probably specific to Authorize.net
 *       Some refactoring might be needed in the future.
 *
 * @param {Object} data - the message data
 * @property {*} data.payload - the message, sent from the iframe
 * @property {string} data.referrer - the referrer of the message
 */
function onPaymentMessageReceived(data) {
	const ticketing = Store.get().showtimes.ticketing;
	const { paymentGateway } = ticketing;

	switch (paymentGateway) {
		case gateways.AUTHNET:    handleAuthnetPaymentMessage(data, ticketing);   break;
		case gateways.VANTIV:     handleVantivPaymentMessage(data, ticketing);    break;
		case gateways.PXPAY:      handlePxPayPaymentMessage(data, ticketing);     break;
		case gateways.VERIFONE2:  handleVerifone2PaymentMessage(data, ticketing); break;
		case gateways.BRAINTREE:  handleBraintreePaymentMessage(data, ticketing); break;
		case gateways.MASTERCARD: handleMastercardPaymentMessage(data, ticketing); break;
		default: console.error(`updateTicketing: unkown payment gateway "${paymentGateway}", ` +
			'discarding payment message');
	}
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Ticketing timeout

/**
 * Display an error with the timeout message, and cancel the order.
 * This function clears the ticketing session.
 */
function goToTicketingTimeout() {
	track({ a: 'ticketing-go-timeout' });

	const ticketing = Store.get().showtimes.ticketing;
	const message = getCopy('TICKETING_TIMEOUT_MESSAGE', {
		MIN: Math.round(ticketing.allowedTime / 60)
	});

	goToError(message);
}


// =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=
// Error in ticketing

/**
 * Function called when an HTTP error occurs
 *
 * @param {Object} error - the error object returned by fetch
 */
function onHttpError(error) {
	let copy = '$UNKNOW_ERROR';

	// TODO: Track error numbers
	// TODO: Branch based on error gravity (display message or error panel)

	if (isMxErrorObject(error)) {
		const { errorCode } = error.data.error;
		copy = '$MX_' + getMxErrorKey(errorCode);
		track({
			a: 'ticketing-error-from-mx',
			l: errorCode
		});
	} else if (isPowErrorObject(error)) {
		const { code } = error.data.error;

		if (code === powErrors.AUTH_FAIL || code === powErrors.AUTH_EXPIRED) {
			Store.emit(actions.AUTH_CLEAR);
		}

		copy = '$POW_' + getPowErrorKey(code);
		track({
			a: 'ticketing-error-from-pow',
			l: code
		});
	}

	goToError(copy);
}

/**
 * Display the "ERROR" pane.
 * This function clears the ticketing session.
 *
 * Should only be used in extreme cases, where you know won't be able to handle the error.
 *
 * @param {string=} message - the error message to show to the user
 */
function goToError(message) {
	track({ a: 'ticketing-error' });

	clearTicketingSession();

	const ticketing = Store.get().showtimes.ticketing;

	ticketing.set({
		state:     ERROR,
		message:   message,
		prevState: ticketing.state
	});
}
